import { Navigation, Users, Maximize2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useWardStore } from '@/stores/wardStore'
import { useUIStore } from '@/stores/uiStore'
import { useDirectionsStore } from '@/stores/directionsStore'
import { VI_MESSAGES } from '@/constants/vietnamese'
import { formatNumber } from '@/utils/vietnamese-format'
import type { WardInfo } from '@/types/ward'

interface WardInfoSheetProps {
  ward: WardInfo
}

export function WardInfoSheet({ ward }: WardInfoSheetProps) {
  const selectWard = useWardStore((state) => state.selectWard)
  const setActiveTab = useUIStore((state) => state.setActiveTab)
  const setDestination = useDirectionsStore((state) => state.setDestination)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      selectWard(null)
    }
  }

  const handleDirections = () => {
    setDestination(ward.xaphuong)
    setActiveTab('directions')
    selectWard(null) // Close sheet
  }

  return (
    <Sheet open={!!ward} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="h-[65vh] bg-white">
        <SheetHeader>
          <SheetTitle className="text-2xl">{ward.xaphuong}</SheetTitle>
          <SheetDescription>{ward.xaphuong}, Hà Nội</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Statistics Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Dân số */}
            {ward.danso && (
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{VI_MESSAGES.WARD.LABELS.POPULATION}</p>
                  <p className="text-xl font-bold">{formatNumber(ward.danso)}</p>
                  <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.UNITS.POPULATION}</p>
                </div>
              </div>
            )}

            {/* Diện tích */}
            {ward.dientichpl && (
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-green-100 p-2">
                  <Maximize2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{VI_MESSAGES.WARD.LABELS.AREA}</p>
                  <p className="text-xl font-bold">{formatNumber(ward.dientichpl, 2)}</p>
                  <p className="text-xs text-gray-500">km²</p>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* BITHU Info */}
          {ward.bithu && (
            <div>
              <h4 className="mb-3 text-lg font-semibold">{VI_MESSAGES.WARD.LABELS.BITHU}</h4>
              <p className="text-lg">{ward.bithu}</p>
            </div>
          )}

          {/* Chairman Info */}
          {ward.chutich && (
            <div>
              <h4 className="mb-3 text-lg font-semibold">{VI_MESSAGES.WARD.LABELS.CHAIRMAN}</h4>
              <p className="text-lg">{ward.chutich}</p>
            </div>
          )}

          {/* MATTRAN Info */}
          {ward.mattran && (
            <div>
              <h4 className="mb-3 text-lg font-semibold">{VI_MESSAGES.WARD.LABELS.MTTQ}</h4>
              <p className="text-lg">{ward.mattran}</p>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4">
            <Button onClick={handleDirections} className="w-full" size="lg">
              <Navigation className="mr-2 h-5 w-5" />
              {VI_MESSAGES.WARD.ACTIONS.GET_DIRECTIONS}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
